/**
 * FunnelSection — Conversion funnel visualization for the Admin dashboard.
 *
 * Shows:
 * - Key metric cards (Total Users, Paying Users, Conversion Rate)
 * - Funnel step bars with conversion/dropoff rates
 * - Recent funnel events table
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Users, CreditCard, Percent } from "lucide-react";
import { trpc } from "@/lib/trpc";

/** Human-readable labels for funnel event names */
const STEP_LABELS: Record<string, string> = {
  page_view: "Page View",
  signup_start: "Signup Started",
  signup_complete: "Signup Complete",
  file_upload: "File Uploaded",
  extraction_complete: "Extraction Complete",
  profile_review: "Profile Reviewed",
  company_committed: "Company Committed",
  first_report_generated: "First Report",
  payment_initiated: "Payment Started",
  payment_completed: "Payment Completed",
};

function formatStepName(name: string): string {
  return STEP_LABELS[name] ?? name.replace(/_/g, " ");
}

export function FunnelSection() {
  const funnelStats = trpc.funnel.getFunnelStats.useQuery({ days: 30 });
  const recentEvents = trpc.funnel.getRecentEvents.useQuery({ limit: 20 });

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <TrendingUp className="h-6 w-6 text-[var(--primary)]" />
        <div>
          <h2 className="text-xl font-semibold">Conversion Funnel</h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            Onboarding to payment — last 30 days
          </p>
        </div>
      </div>

      {/* Key metric cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-[var(--muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {funnelStats.isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {funnelStats.data?.totalUsers.toLocaleString() ?? 0}
              </div>
            )}
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              Completed signup
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paying Users</CardTitle>
            <CreditCard className="h-4 w-4 text-[var(--muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {funnelStats.isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {funnelStats.data?.totalPayingUsers.toLocaleString() ?? 0}
              </div>
            )}
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              Completed payment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Conversion Rate
            </CardTitle>
            <Percent className="h-4 w-4 text-[var(--muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {funnelStats.isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {(funnelStats.data?.conversionRate ?? 0).toFixed(1)}%
              </div>
            )}
            <p className="text-xs text-[var(--muted-foreground)] mt-1">
              Signup to payment
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel visualization */}
      <Card>
        <CardHeader>
          <CardTitle>Funnel Steps</CardTitle>
          <CardDescription>
            Unique users at each step — bars proportional to first step
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funnelStats.isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {funnelStats.data?.steps.map((step, i) => (
                <div key={step.name} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">
                      {formatStepName(step.name)}
                    </span>
                    <span className="text-[var(--muted-foreground)]">
                      {step.count.toLocaleString()} (
                      {step.conversionRate.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="h-8 rounded bg-[var(--muted)]">
                    <div
                      className="h-full rounded bg-[var(--primary)] transition-all duration-500"
                      style={{
                        width: `${Math.max(step.conversionRate, 0.5)}%`,
                      }}
                    />
                  </div>
                  {i > 0 && step.dropoffRate > 0 && (
                    <p className="text-xs text-[var(--muted-foreground)]">
                      -{step.dropoffRate.toFixed(1)}% from previous step
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent events table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Events</CardTitle>
          <CardDescription>Last 20 funnel events</CardDescription>
        </CardHeader>
        <CardContent>
          {recentEvents.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Session</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentEvents.data?.map(evt => (
                  <TableRow key={evt.id}>
                    <TableCell>
                      <Badge variant="secondary">
                        {formatStepName(evt.eventName)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {evt.userName || evt.userEmail || (
                        <span className="text-[var(--muted-foreground)]">
                          Anonymous
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-[var(--muted-foreground)] font-mono">
                      {evt.sessionId?.substring(0, 8) ?? "-"}
                    </TableCell>
                    <TableCell className="text-xs text-[var(--muted-foreground)]">
                      {evt.createdAt
                        ? new Date(evt.createdAt).toLocaleString("de-CH")
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
                {(!recentEvents.data || recentEvents.data.length === 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-[var(--muted-foreground)]"
                    >
                      No funnel events recorded yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
